// ═══════════════════════════════════════════════════════════════
// Enterprise Messaging — Query Layer
// ═══════════════════════════════════════════════════════════════

import { supabase } from '../supabase';
import type {
  MsgChannel, MsgChannelMember, MsgMessage, MsgReaction,
  MsgAttachment, MsgSearchResult, ChannelUnread,
  CreateChannelInput, SendMessageInput, UpdateMessageInput,
} from './types';

// ─── Channels ────────────────────────────────────────────────

export async function getChannels(companyId?: string): Promise<MsgChannel[]> {
  let query = supabase
    .from('msg_channels')
    .select(`
      *,
      members:msg_channel_members(
        id, user_id, role, is_muted, last_read_at, joined_at,
        profile:profiles!msg_channel_members_user_id_fkey(id, full_name, avatar_url, role)
      )
    `)
    .eq('is_archived', false)
    .order('last_message_at', { ascending: false, nullsFirst: false });
  // RLS already filters by company membership via get_user_company_ids()
  // Only add company filter when explicitly needed (not in consolidated view)
  if (companyId) query = query.eq('company_id', companyId);
  const { data, error } = await query;
  if (error) throw error;
  return data ?? [];
}

export async function getChannel(channelId: string): Promise<MsgChannel> {
  const { data, error } = await supabase
    .from('msg_channels')
    .select(`
      *,
      members:msg_channel_members(
        id, user_id, role, is_muted, last_read_at, joined_at,
        profile:profiles!msg_channel_members_user_id_fkey(id, full_name, avatar_url, role)
      )
    `)
    .eq('id', channelId)
    .single();
  if (error) throw error;
  return data;
}

export async function createChannel(input: CreateChannelInput): Promise<string> {
  const { member_ids, ...channelData } = input;
  const { data, error } = await supabase
    .from('msg_channels')
    .insert(channelData)
    .select('id')
    .single();
  if (error) throw error;

  const members = member_ids.map((uid, i) => ({
    channel_id: data.id,
    user_id: uid,
    role: uid === input.created_by ? 'owner' : 'member',
  }));
  const { error: mErr } = await supabase.from('msg_channel_members').insert(members);
  if (mErr) throw mErr;

  return data.id;
}

export async function updateChannel(channelId: string, updates: Partial<Pick<MsgChannel, 'name' | 'name_ar' | 'description' | 'is_private' | 'is_readonly' | 'is_archived'>>): Promise<void> {
  const { error } = await supabase
    .from('msg_channels')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', channelId);
  if (error) throw error;
}

export async function archiveChannel(channelId: string): Promise<void> {
  await updateChannel(channelId, { is_archived: true });
}

// ─── Channel Members ─────────────────────────────────────────

export async function addChannelMembers(channelId: string, userIds: string[], role = 'member'): Promise<void> {
  const members = userIds.map(uid => ({ channel_id: channelId, user_id: uid, role }));
  const { error } = await supabase.from('msg_channel_members').upsert(members, { onConflict: 'channel_id,user_id' });
  if (error) throw error;
}

export async function removeChannelMember(channelId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from('msg_channel_members')
    .delete()
    .eq('channel_id', channelId)
    .eq('user_id', userId);
  if (error) throw error;
}

export async function updateMemberRole(channelId: string, userId: string, role: string): Promise<void> {
  const { error } = await supabase
    .from('msg_channel_members')
    .update({ role })
    .eq('channel_id', channelId)
    .eq('user_id', userId);
  if (error) throw error;
}

export async function toggleMute(channelId: string, userId: string, muted: boolean): Promise<void> {
  const { error } = await supabase
    .from('msg_channel_members')
    .update({ is_muted: muted })
    .eq('channel_id', channelId)
    .eq('user_id', userId);
  if (error) throw error;
}

// ─── Messages ────────────────────────────────────────────────

export async function getMessages(channelId: string, limit = 50, before?: string): Promise<MsgMessage[]> {
  let query = supabase
    .from('msg_messages')
    .select(`
      *,
      sender:profiles!msg_messages_sender_id_fkey(id, full_name, avatar_url),
      reactions:msg_reactions(id, user_id, emoji, created_at),
      attachments:msg_attachments(id, file_name, file_size, mime_type, storage_path, uploaded_by, created_at)
    `)
    .eq('channel_id', channelId)
    .eq('is_deleted', false)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (before) {
    query = query.lt('created_at', before);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []).reverse();
}

export async function getThreadMessages(parentId: string): Promise<MsgMessage[]> {
  const { data, error } = await supabase
    .from('msg_messages')
    .select(`
      *,
      sender:profiles!msg_messages_sender_id_fkey(id, full_name, avatar_url),
      reactions:msg_reactions(id, user_id, emoji, created_at)
    `)
    .eq('thread_parent_id', parentId)
    .eq('is_deleted', false)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

export async function sendMessage(input: SendMessageInput): Promise<string> {
  const { data, error } = await supabase
    .from('msg_messages')
    .insert({
      channel_id: input.channel_id,
      sender_id: input.sender_id,
      content: input.content,
      content_ar: input.content_ar,
      message_type: input.message_type ?? 'text',
      thread_parent_id: input.thread_parent_id,
      metadata: input.metadata,
    })
    .select('id')
    .single();
  if (error) throw error;
  return data.id;
}

export async function editMessage(input: UpdateMessageInput): Promise<void> {
  const { error } = await supabase
    .from('msg_messages')
    .update({
      content: input.content,
      content_ar: input.content_ar,
      is_edited: true,
      edited_at: new Date().toISOString(),
    })
    .eq('id', input.id);
  if (error) throw error;
}

export async function deleteMessage(messageId: string, deletedBy: string): Promise<void> {
  const { error } = await supabase
    .from('msg_messages')
    .update({
      is_deleted: true,
      deleted_at: new Date().toISOString(),
      deleted_by: deletedBy,
    })
    .eq('id', messageId);
  if (error) throw error;
}

// ─── Reactions ───────────────────────────────────────────────

export async function addReaction(messageId: string, userId: string, emoji: string): Promise<void> {
  const { error } = await supabase
    .from('msg_reactions')
    .upsert({ message_id: messageId, user_id: userId, emoji }, { onConflict: 'message_id,user_id,emoji' });
  if (error) throw error;
}

export async function removeReaction(messageId: string, userId: string, emoji: string): Promise<void> {
  const { error } = await supabase
    .from('msg_reactions')
    .delete()
    .eq('message_id', messageId)
    .eq('user_id', userId)
    .eq('emoji', emoji);
  if (error) throw error;
}

// ─── Attachments ─────────────────────────────────────────────

export async function uploadAttachment(
  file: File,
  messageId: string,
  uploadedBy: string,
): Promise<MsgAttachment> {
  const ext = file.name.split('.').pop() ?? 'bin';
  const storagePath = `msg/${messageId}/${crypto.randomUUID()}.${ext}`;

  const { error: upErr } = await supabase.storage
    .from('message-attachments')
    .upload(storagePath, file, { contentType: file.type, upsert: false });
  if (upErr) throw new Error(upErr.message);

  const { data, error } = await supabase
    .from('msg_attachments')
    .insert({
      message_id: messageId,
      file_name: file.name,
      file_size: file.size,
      mime_type: file.type,
      storage_path: storagePath,
      uploaded_by: uploadedBy,
    })
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

export async function getAttachmentUrl(storagePath: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from('message-attachments')
    .createSignedUrl(storagePath, 60 * 60); // 1 hour
  if (error) throw new Error(error.message);
  return data.signedUrl;
}

// ─── Read Tracking ───────────────────────────────────────────

export async function markChannelRead(channelId: string, userId: string): Promise<void> {
  const { error } = await supabase.rpc('mark_channel_as_read', {
    p_channel_id: channelId,
    p_user_id: userId,
  });
  if (error) throw error;
}

export async function getUnreadCounts(userId: string): Promise<ChannelUnread[]> {
  const { data, error } = await supabase.rpc('get_user_channel_unread_totals', {
    p_user_id: userId,
  });
  if (error) throw error;
  return (data ?? []) as ChannelUnread[];
}

// ─── DM Helper ───────────────────────────────────────────────

export async function getOrCreateDM(userA: string, userB: string, companyId: string): Promise<string> {
  const { data, error } = await supabase.rpc('get_or_create_dm_channel', {
    p_user_a: userA,
    p_user_b: userB,
    p_company_id: companyId,
  });
  if (error) throw error;
  return data as string;
}

// ─── Search ──────────────────────────────────────────────────

export async function searchMessages(
  query: string,
  companyId: string,
  userId: string,
  limit = 20,
  offset = 0,
): Promise<MsgSearchResult[]> {
  const { data, error } = await supabase.rpc('search_messages', {
    p_query: query,
    p_company_id: companyId,
    p_user_id: userId,
    p_limit: limit,
    p_offset: offset,
  });
  if (error) throw error;
  return (data ?? []) as MsgSearchResult[];
}
